import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { DataTable } from 'primereact/datatable'
import { InputText } from 'primereact/inputtext'
import { StepperRefAttributes } from 'primereact/stepper'

import {
  AttachmentDeleteButton,
  AttachmentDownloadButton,
  AttachmentOpenInTabButton,
} from '../../../../../../components/attachments'
import { useToastContext } from '../../../../../../contexts'
import { EInvoiceAttachment, EInvoiceDto } from '../../../../../../data/models'
import { getFormattedFileSize } from '../../../../../../utils/formatUtils'
import i18n from '../../../../../../utils/i18n'
import { VirusCheckStatus } from '../../../../common'
import {
  EInvoiceError,
  requestDeleteEInvoiceAttachment,
  requestUpdateEInvoiceAttachment,
  requestValidateEInvoice,
  useEInvoiceById,
} from '../../../../data'
import { Footer } from '../footer'
import Upload from './dialog/Upload'
import { VerificationDialog } from './dialog/VerificationDialog'

type Props = {
  eInvoice?: EInvoiceDto
  stepperRef: React.MutableRefObject<StepperRefAttributes | null>
  setVerificationError: React.Dispatch<
    React.SetStateAction<EInvoiceError | undefined>
  >
}

export const AdditionalDocuments: React.FC<Props> = ({
  eInvoice,
  stepperRef,
  setVerificationError,
}) => {
  const { t } = useTranslation()
  const { replaceToast } = useToastContext()
  const { mutate } = useEInvoiceById(eInvoice?.id!)
  const [editingRowId, setEditingRowId] = useState<string>()
  const [editedDescription, setEditedDescription] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [locationUrl, setLocationUrl] = useState<string>('')
  const [retry, setRetry] = useState<string>('')
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false)

  const headerActions = (
    <Upload invoiceId={eInvoice?.id} processId={eInvoice?.ProcessId!} />
  )

  const attachmentsForTable =
    eInvoice?.Attachments?.filter(
      att =>
        att.DocumentType === 'EmbeddedDocument' ||
        att.DocumentType === 'AdditionalDocument',
    ) ?? []
  const fileSizeTemplate = (rowData: EInvoiceAttachment) => {
    return <>{getFormattedFileSize(rowData.FileSizeInBytes, i18n.language)}</>
  }

  const documentTypeTemplate = (rowData: EInvoiceAttachment) => {
    return (
      <>
        {t(
          `eInvoiceDetails.stepper.additionalDocuments.table.columns.documentType.options.${rowData.DocumentType}`,
        )}
      </>
    )
  }

  const isVirusCheckedTemplate = (rowData: EInvoiceAttachment) => {
    return <VirusCheckStatus virusChecked={rowData.VirusChecked} />
  }

  const handleVerification = async () => {
    // Check if validation is needed
    const needsValidation =
      eInvoice?.Meta?.file.status === 'UnverifiedInvoice' ||
      eInvoice?.Meta?.file.status === 'FailedRulesEngineCheck'

    // If no validation needed, allow normal navigation
    if (!needsValidation) {
      return true // Allow navigation to next step
    }

    // Validation needed - open dialog and prevent automatic navigation
    setIsDialogVisible(true)

    try {
      const response = await requestValidateEInvoice(eInvoice?.id!)

      // Set location url
      if (response && response.locationUrl) {
        setLocationUrl(response.locationUrl)
      }

      // Set retry
      if (response && response.retry) {
        setRetry(response.retry)
      }

      // Return false to prevent automatic navigation - dialog will handle it
      return false
    } catch (error: any) {
      setIsDialogVisible(false)
      replaceToast({
        severity: 'error',
        sticky: true,
        summary: t(
          'eInvoiceDetails.stepper.additionalDocuments.verify.dialog.messages.error.summary',
        ),
        detail: t(
          'eInvoiceDetails.stepper.additionalDocuments.verify.dialog.messages.error.detail',
        ),
      })

      // Return false to prevent navigation on error
      return false
    }
  }

  const onDeleteAttachment = async (
    attachmentId: string,
    attachmentUri: string,
  ) => {
    setIsDeleting(true)
    try {
      await requestDeleteEInvoiceAttachment(
        eInvoice?.id!,
        attachmentId,
        attachmentUri,
      )
    } finally {
      setIsDeleting(false)
    }
  }

  const onEditStart = (rowData: EInvoiceAttachment) => {
    setEditingRowId(rowData.Id!)
    setEditedDescription(rowData.Description || '')
  }

  const onEditCancel = () => {
    setEditingRowId('')
    setEditedDescription('')
  }

  const onEditConfirm = async (rowData: EInvoiceAttachment) => {
    setIsSaving(true)
    try {
      const editedAttachment = {
        ...rowData,
        Description: editedDescription,
      }
      await requestUpdateEInvoiceAttachment(
        eInvoice?.id!,
        rowData.Id!,
        editedAttachment,
      )

      replaceToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t(
          'eInvoiceDetails.stepper.additionalDocuments.table.confirm.update.success.summary',
        ),
        detail: t(
          'eInvoiceDetails.stepper.additionalDocuments.table.confirm.update.success.detail',
        ),
      })

      setEditingRowId('')
      setEditedDescription('')
    } finally {
      setIsSaving(false)
    }
  }

  const actionBodyTemplate = (rowData: EInvoiceAttachment) => {
    const isEditing = editingRowId === rowData.Id
    const isDisabled = isSaving || isDeleting

    if (isEditing) {
      return (
        <div className="flex justify-content-end gap-2">
          <Button
            icon="pi pi-check"
            rounded
            text
            severity="success"
            aria-label={t(
              'eInvoiceDetails.stepper.additionalDocuments.table.confirm.label',
            )}
            onClick={() => onEditConfirm(rowData)}
            loading={isSaving}
            disabled={isDisabled}
            tooltipOptions={{ position: 'top' }}
          />
          <Button
            icon="pi pi-times"
            rounded
            text
            severity="secondary"
            aria-label={t(
              'eInvoiceDetails.stepper.additionalDocuments.table.cancel.label',
            )}
            onClick={onEditCancel}
            disabled={isDisabled}
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      )
    }

    return (
      <div className="flex justify-content-end gap-2">
        <AttachmentDeleteButton
          key={`att-delete-${rowData.Id}`}
          fileName={rowData.FileName}
          attachmentId={rowData.Id!}
          attachmentUri={rowData.DocServUri!}
          onDelete={onDeleteAttachment}
          visible={rowData.DocumentType !== 'EmbeddedDocument'}
          disabled={isDisabled}
        />

        <Button
          icon="pi pi-pencil"
          rounded
          text
          aria-label={t(
            'eInvoiceDetails.stepper.additionalDocuments.table.edit.label',
          )}
          onClick={() => onEditStart(rowData)}
          tooltipOptions={{ position: 'top' }}
          visible={rowData.DocumentType !== 'EmbeddedDocument'}
          disabled={isDisabled}
        />

        <AttachmentOpenInTabButton
          key={`att-tab-${rowData.Id}`}
          fullUrl={rowData.DocServUri as string}
          mimeType={rowData.MimeType as string}
          fileName={rowData.FileName}
          disabled={isDisabled}
        />

        <AttachmentDownloadButton
          key={`att-download-${rowData.Id}`}
          fullUrl={rowData.DocServUri as string}
          mimeType={rowData.MimeType as string}
          fileName={rowData.FileName}
          disabled={isDisabled}
        />
      </div>
    )
  }

  const descriptionBodyTemplate = (rowData: EInvoiceAttachment) => {
    const isEditing = editingRowId === rowData.Id

    if (isEditing) {
      return (
        <InputText
          type="text"
          value={editedDescription}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEditedDescription(e.target.value)
          }
          className="w-full"
        />
      )
    }

    return <span>{rowData.Description}</span>
  }

  const header = () => {
    return (
      <div className="table-header flex justify-content-between flex-wrap align-items-center">
        <h3 className="mx-0 my-1">
          {t(
            'eInvoiceDetails.stepper.summary.documents.additionalDocuments.title',
          )}
        </h3>
        <div className="flex flex-wrap gap-2">{headerActions}</div>
      </div>
    )
  }

  return (
    <>
      <ConfirmDialog />

      <VerificationDialog
        locationUrl={locationUrl}
        retry={retry}
        isDialogVisible={isDialogVisible}
        setIsDialogVisible={setIsDialogVisible}
        stepperRef={stepperRef}
        setVerificationError={setVerificationError}
        onTaskComplete={mutate}
      />

      <div>
        <h2>{t('eInvoiceDetails.stepper.additionalDocuments.description')}</h2>
        <DataTable
          className="mb-6"
          value={attachmentsForTable}
          header={header}
          emptyMessage={t(
            'eInvoiceDetails.stepper.additionalDocuments.table.emptyMessage',
          )}
        >
          <Column
            field="FileName"
            header={t(
              'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.fileName.header',
            )}
          />
          <Column
            field="Description"
            header={t(
              'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.description.header',
            )}
            body={descriptionBodyTemplate}
          />
          <Column
            header={t(
              'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.documentType.header',
            )}
            body={documentTypeTemplate}
          />
          <Column
            header={t(
              'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.fileSize.header',
            )}
            body={fileSizeTemplate}
          />
          <Column
            header={t(
              'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.virusChecked.header',
            )}
            body={isVirusCheckedTemplate}
            align="center"
          />
          <Column
            header={t(
              'eInvoiceDetails.stepper.summary.documents.additionalDocuments.table.columns.attachment.header',
            )}
            key="attachmentButtons"
            body={actionBodyTemplate}
            align="right"
          />
        </DataTable>
      </div>
      <Footer stepperRef={stepperRef} nextButtonCallback={handleVerification} />
    </>
  )
}
